import type { User, UserResponse } from "@supabase/supabase-js";
import { get, writable, type Writable } from "svelte/store";
import { supabase } from "../lib/supabase";
import { localStorageWritable } from "./localStorageWritable";

// User store
export const user: Writable<User | null> = writable();

export const isSubscribedToJobAlert = localStorageWritable(
	"isSubscribedToJobAlert",
	false
);

export const getUser = async () => {
	const hasSession = await supabase.auth.getSession();

	if (!hasSession) {
		user.set(null);
		return;
	}

	if (get(user)) {
		return get(user);
	} else {
		const result = await supabase.auth.getUser();

		if (result.error || !result.data.user) {
			return null;
		}
		user.set(result.data.user);
		return result.data.user;
	}
};

export const signOut = async () => {
	await supabase.auth.signOut();
	user.set(null);
};

export const updateUser = async (input: {
	data?: {
		phone?: string | null;
		factorIds?: number[] | null;
		sectorIds?: number[] | null;
		firstName?: string | null;
		lastName?: string | null;
		email?: string | null;
	};
}) => {
	const currentSession = await supabase.auth.getSession();

	// Create an options object for the updateUser call
	let updateOptions: any = {
		data: {
			...currentSession.data.session?.user.user_metadata
		}
	};

	// Handle metadata updates for factors, sectors, firstName, lastName
	if (input?.data?.factorIds) {
		updateOptions.data.factorIds = input.data.factorIds;
	}
	if (input?.data?.sectorIds) {
		updateOptions.data.sectorIds = input.data.sectorIds;
	}
	if (input?.data?.firstName) {
		updateOptions.data.firstName = input.data.firstName;
	}
	if (input?.data?.lastName) {
		updateOptions.data.lastName = input.data.lastName;
	}

	// Handle email update as a direct property (not metadata)
	if (input?.data?.email) {
		// Create a separate update for email to avoid conflicts
		const emailUpdateOptions = {
			email: input.data.email,
			options: {
				emailRedirectTo: window.location.origin
			}
		};

		console.log("Email update options:", emailUpdateOptions);

		try {
			const emailUpdate = await supabase.auth.updateUser(emailUpdateOptions);
			if (emailUpdate.error) {
				console.error("Email update error:", emailUpdate.error);
				return {
					error: true,
					success: false,
					data: {
						user: emailUpdate.data.user,
						error: emailUpdate.error
					}
				};
			}
		} catch (err) {
			console.error("Exception during email update:", err);
			return {
				error: true,
				success: false,
				data: {
					error: {
						message:
							err instanceof Error
								? err.message
								: "Unknown error during email update"
					}
				}
			};
		}
	}

	// Handle phone update - Supabase requires phone to be in E.164 format
	// For now, we'll keep phone in metadata to avoid format issues
	if (input?.data?.phone) {
		updateOptions.data.phone = input.data.phone;
	}

	// Only update metadata if there are changes to make
	const hasMetadataChanges =
		input?.data?.firstName ||
		input?.data?.lastName ||
		input?.data?.phone ||
		input?.data?.factorIds ||
		input?.data?.sectorIds;

	if (hasMetadataChanges) {
		console.log(
			"Metadata update options being sent to Supabase:",
			updateOptions
		);

		try {
			const { data, error } = await supabase.auth.updateUser(updateOptions);

			if (error) {
				console.error("Supabase updateUser error:", error);
				return {
					error: true,
					success: false,
					data: {
						user: data.user,
						error: error
					}
				};
			}

			user.set(data.user);

			return {
				error: false,
				success: true,
				data
			};
		} catch (err) {
			console.error("Exception during metadata update:", err);
			return {
				error: true,
				success: false,
				data: {
					error: {
						message:
							err instanceof Error
								? err.message
								: "Unknown error during metadata update"
					}
				}
			};
		}
	} else if (input?.data?.email) {
		// If we only updated email and it was successful, return success
		return {
			error: false,
			success: true
		};
	} else {
		// No changes to make
		return {
			error: false,
			success: true
		};
	}
};

export const isProfileComplete = async (user: UserResponse) => {
	if (!user.data.user?.user_metadata?.firstName) {
		return false;
	}
	if (!user.data.user?.user_metadata?.lastName) {
		return false;
	}

	return true;
};
